import cx from 'classnames';
import { kebabCase } from 'lodash-es';
import { appendGuestCountLabel } from '@shared/utils/guestCount';
import { formatPhone } from '@utils/formatPhone';
import { ISOTimeTo12HourTime } from '@utils/time';
import typography from '~styles/typography.scss';
import { type GuestAttributes, type OccupantType } from '../../apiHelpers';
import styles from './CoversReportItem.scss';

interface CoversReportItemProps {
  guest: GuestAttributes;
  guestCount: number;
  guestNotes?: string;
  guestTags?: string[];
  hostBookedBy?: string;
  isStranded?: boolean;
  listingName?: string;
  staffNote?: string;
  time: string;
  type?: OccupantType;
}

export const CoversReportItem = ({
  guest: { email, firstName, lastName, phone },
  guestCount,
  guestNotes,
  guestTags,
  hostBookedBy,
  isStranded,
  listingName,
  staffNote,
  time,
  type = 'reservation',
}: CoversReportItemProps) => (
  <li className={styles.occupant}>
    <section>
      <p className={typography.h5}>
        {ISOTimeTo12HourTime(time)} {kebabCase(type)}
      </p>
      <p className={cx(typography.c2, styles.stranded)}>
        {isStranded && <span>UNASSIGNED</span>}
      </p>
      <p className={typography.c2}>{appendGuestCountLabel(guestCount)}</p>
      <p className={typography.c2}>{listingName}</p>
      {hostBookedBy && (
        <p className={cx(typography.t1, styles.fillRow, styles.booker)}>
          Booked by {hostBookedBy}
        </p>
      )}
    </section>
    <section>
      <p className={typography.h5}>{`${firstName} ${lastName}`}</p>
      {phone && (
        <p className={cx(typography.c2, styles.fillRow)}>
          <a href={`tel:${phone}`}>{formatPhone(phone)}</a>
        </p>
      )}
      {email && (
        <p className={cx(typography.c2, styles.fillRow)}>
          <a href={`mailto:${email}`}>{email}</a>
        </p>
      )}
      {guestTags && <GuestTags tags={guestTags} />}
      {guestNotes && (
        <p className={cx(styles.fillRow, styles.notes)}>
          Guest notes: {guestNotes}
        </p>
      )}
      {staffNote && (
        <p className={cx(styles.fillRow, styles.notes)}>
          Visit notes: {staffNote}
        </p>
      )}
    </section>
  </li>
);

const GuestTags = ({ tags }: { tags: string[] }) => (
  <ul className={styles.tagList}>
    {tags.map((tag: string) => {
      const tagClass = cx(typography.t1, {
        [styles.tagVIP]: tag.toLowerCase() === 'vip',
        [styles.tagStranded]: tag.toLowerCase() === 'stranded',
      });

      return (
        <li key={tag} className={tagClass}>
          {tag}
        </li>
      );
    })}
  </ul>
);
